import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  ExtensionMethod,
  FileUploadEvent,
} from '@flywheel-io/extension';
import { Channel, ChannelMessage, ChannelMethod } from '@flywheel-io/extension/lib/channel';
import { serializeError } from '@flywheel-io/extension/lib/data';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, filter, first, map, mapTo, switchMap } from 'rxjs/operators';

import { ConfigService } from '../../config.service';
import { CoreApiService, queryAll } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { ProfileService } from '../../profile/profile.service';
import { ProjectsService } from '../../projects/projects.service';
import {
  getContainerProjectId,
  getContainerType,
  isContainerType,
  pluralize,
} from '../../shared/container.utils';
import { ContainerType } from '../../shared/enums/container-type.enum';
import { PermissionAction, permissionForCollection } from '../../shared/enums/permission-action.enum';
import { Container } from '../../shared/models/container.model';
import { File } from '../../shared/models/file.model';
import { Profile } from '../../shared/models/profile.model';
import { ProjectSettings } from '../../shared/models/project.model';
import { SiteSettings } from '../../shared/models/site-settings.model';
import { paramsToQueryString } from '../../shared/url.utils';
import { ViewerParameters, ViewerTextConfig } from './viewer-frame.model';

@Component({
  selector: 'viewer-frame',
  template: `
    <iframe #frame [src]="safeFrameUrl" allow="fullscreen; autoplay; clipboard-read; clipboard-write" [ngStyle]="{'height.px': frameHeight}"></iframe>
  `,
  styles: [`
    :host {
      display: block;
    }
    iframe {
      border: none;
      display: block;
      height: calc(100vh - 70px);
      width: 100%;
    }
  `],
})
export class ViewerFrameComponent implements OnInit, OnDestroy {
  static writableMethods = new Set([
    ExtensionMethod.UpdateInfo,
    ExtensionMethod.UploadFile,
    ExtensionMethod.UploadText,
    ExtensionMethod.WriteSetting,
    ExtensionMethod.AddJob,
    ExtensionMethod.AddAnalysis,
    ExtensionMethod.AddTag,
    ExtensionMethod.RemoveTag,
  ]);

  @Input() applicationUrl: string
  @Input() container: Container
  @Input() file?: File
  @Input() text?: ViewerTextConfig
  @Input() textSaved?: Observable<void>
  @Input() saveText?: (text: string) => Observable<void>
  @Input() params?: ViewerParameters

  @Output() close = new EventEmitter<void>()
  @Output() updateWidth = new EventEmitter<number | string>()

  @ViewChild('frame', { static: true }) frame: ElementRef<HTMLIFrameElement>

  frameHeight: number
  safeFrameUrl: SafeResourceUrl

  private archVersion = this.config.isFeatureEnabled('crawler') ? 3 : 2
  private channel: Channel
  private currentUser: Profile
  private _dirty = false
  get dirty(): boolean {
    return this._dirty;
  }
  private frameUrl: URL

  constructor(
    private config: ConfigService,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private profile: ProfileService,
    private projects: ProjectsService,
    private sanitizer: DomSanitizer,
  ) { }

  ngOnInit(): void {
    // Prefer the CDN proxy, but V2 architecture doesn't support it
    // site.proxy_cdn was introduced later so assume enabled if undefined
    const proxyEnabled = this.archVersion === 3 && this.config.apiConfig.site.proxy_cdn !== false;
    let appUrl = this.applicationUrl;
    if (appUrl.startsWith('https://cdn.flywheel.io/') && proxyEnabled) {
      appUrl = appUrl.replace('https://cdn.flywheel.io/', '/cdn/');
    } else if (appUrl.startsWith('/cdn/') && !proxyEnabled) {
      appUrl = appUrl.replace('/cdn/', 'https://cdn.flywheel.io/');
    }

    this.frameUrl = new URL(appUrl, window.location.origin);
    this.safeFrameUrl = this.sanitizer.bypassSecurityTrustResourceUrl(appUrl);
    this.frame.nativeElement.addEventListener('load', () => {
      if (this.channel) {
        this.channel.destroy();
      }
      this.channel = new Channel({
        receiveWindow: window,
        sendWindow: this.frame.nativeElement.contentWindow,
        targetUrl: this.frameUrl,
      });
      this.channel.messages$.subscribe(this.handleMessage);
      this.channel.connect();
    });
  }

  ngOnDestroy(): void {
    if (this.channel) {
      this.channel.destroy();
    }
  }

  private handleMessage = (message: ChannelMessage): void => {
    this.logger.debug('EXTENSION -> FLYWHEEL \n' + message);
    try {
      const response = this.buildResponse(message);
      if (response) {
        response.subscribe({
          next: response => {
            if (ViewerFrameComponent.writableMethods.has(message.method as ExtensionMethod)) {
              this._dirty = true;
            }
            this.channel.send(response.method || message.method, response.params, response.queryId || message.messageId);
          },
          error: error => {
            this.channel.send(message.method, { error: serializeError(error) }, message.messageId);
          },
        });
      }
    } catch (error) {
      this.channel.send(message.method, { error: serializeError(error) }, message.messageId);
    }
  }

  private buildResponse(message: ChannelMessage): Observable<MessageResponse> | void {
    switch (message.method) {
      case ChannelMethod.Connect:
        this.frame.nativeElement.focus();
        return this.sendExtensionData();
      case ChannelMethod.Destroy:
        this.close.emit();
        return;
      case ExtensionMethod.UpdateViewport:
        this.frameHeight = message.params.height;
        this.updateWidth.emit(message.params.width);
        return;
      case ExtensionMethod.GetContainer:
        return this.sendContainer(message);
      case ExtensionMethod.ListContainers:
        return this.sendContainerList(message);
      case ExtensionMethod.ContainerPermissions:
        return this.sendContainerPermissions(message);
      case ExtensionMethod.GetFile:
        return this.sendFile(message);
      case ExtensionMethod.FileBlob:
        return this.sendFileBlob(message);
      case ExtensionMethod.FileUrl:
        return this.sendFileUrl(message);
      case ExtensionMethod.ArchiveMembers:
        return this.sendArchiveMembers(message);
      case ExtensionMethod.ExecuteDataView:
        return this.sendDataView(message);
      case ExtensionMethod.UpdateInfo:
        return this.updateInfo(message);
      case ExtensionMethod.UploadFile:
        return this.uploadFile(message);
      case ExtensionMethod.UploadText:
        return this.uploadText(message);
      case ExtensionMethod.ReadSetting:
        return this.sendSetting(message);
      case ExtensionMethod.WriteSetting:
        return this.writeSetting(message);
      case ExtensionMethod.AddTag:
        return this.addTag(message);
      case ExtensionMethod.AddAnalysis:
        return this.addAnalysis(message);
      case ExtensionMethod.AddJob:
        return this.addJob(message);
      case ExtensionMethod.RemoveTag:
        return this.removeTag(message);
      case ExtensionMethod.ListGears:
        return this.sendGearList(message);
      default:
        throw new Error('Unknown extension method');
    }
  }

  @HostListener('window:resize')
  private updateViewport(): void {
    this.channel.send(ExtensionMethod.UpdateViewport, {
      width: Math.floor(window.innerWidth * 0.98), // 98vw
      height: Math.floor(window.innerHeight * 0.98), // 98vh
    });
  }

  private sendExtensionData(): Observable<MessageResponse> {
    let containerType = null;
    try {
      containerType = getContainerType(this.container);
    }
    catch (e) {
      // text edit mode doesn't need no dang container
    }
    return combineLatest([
      containerType ? this.coreApi.containers.get<Container>(this.container._id) : of(null),
      this.file?.file_id ? this.coreApi.files.get(this.file.file_id, this.file.version ? { version: this.file.version } : {}) : of(null),
      this.profile.getProfile(),
    ]).pipe(
      first(),
      map(([container, file, user]) => {
        this.currentUser = user;
        if (containerType === ContainerType.Analysis && this.file?.file_id) {
          // Analyses inputs are stored separately outputs, check to see if we should use inputs instead
          const isInputFile = container.inputs?.some(({ _id }) => _id === this.file._id);
          if (isInputFile) {
            container.files = container.inputs;
          }
        }
        const output = {
          method: ExtensionMethod.ExtensionData,
          params: {
            container: container ? { ...container, container_type: containerType } : null,
            file: file ? file : container ? getFileFromContainer(this.file, container) : null,
            text: this.text,
            params: this.params,
            user: omit(user, ['api_key', 'api_keys']),
          },
        };
        return output;
      }),
    );
  }

  private sendContainer(message: ChannelMessage): Observable<MessageResponse> {
    const { params: { id } } = message;
    return this.coreApi.containers.get(
      encodeURIComponent(id),
    ).pipe(map(container => ({
      params: { container },
    })));
  }

  private sendContainerList(message: ChannelMessage): Observable<MessageResponse> {
    const { params: { type, query } } = message;
    if (!isContainerType(type)) {
      throw new Error('Not a valid container type');
    }
    return this.coreApi.containers.query(
      encodeURIComponent(pluralize(type)),
      query,
    ).pipe(map(result => ({
      params: { result },
    })));
  }

  private sendContainerPermissions(message: ChannelMessage): Observable<MessageResponse> {
    const { params: { container } } = message;
    const projectId = getContainerProjectId(container);
    const containerType = getContainerType(container);

    if (containerType === ContainerType.Collection) {
      const userPermission = container.permissions.find(permission => permission._id === this.currentUser._id);

      const permissions = permissionForCollection(userPermission?.access);
      return of({ params: { permissions: new Set([permissions]) } });
    }

    return this.projects.getProjectPermissions(projectId).pipe(
      first(),
      map(actions => {
        const result = { params: { permissions: new Set(actions) } };
        if (container.container_type === ContainerType.Analysis) {
          // analysis files are immutable and can only be viewed
          result.params.permissions = new Set([PermissionAction.FilesViewContents]);
        }
        return result;
      }));
  }

  private sendFile(message: ChannelMessage): Observable<MessageResponse> {
    const { params: { fileId, options } } = message;
    return this.coreApi.files.get(
      encodeURIComponent(fileId),
      pick(options, ['version']),
    ).pipe(map(file => ({
      params: { file },
    })));
  }

  private sendFileBlob(message: ChannelMessage): Observable<MessageResponse> {
    const { params: { file, options } } = message;
    let pipeline$: Observable<Blob>;
    if (options?.member) {
      pipeline$ = this.coreApi.files.zipMember(
        encodeURIComponent(file.file_id),
        options.member, // encoded in FlywheelService
        pick(file, ['version']),
        pick(options, ['range']),
      );
    } else {
      // entire file
      pipeline$ = this.coreApi.files.blob(
        encodeURIComponent(file.file_id),
        pick(file, ['version']),
        pick(options, ['range']),
      );
    }
    return pipeline$.pipe(map(blob => ({
      params: { blob },
    })));
  }

  private sendFileUrl(message: ChannelMessage): Observable<MessageResponse> {
    const { params: { file, options } } = message;
    const generateParams = { ...pick(file, ['version']), view: true };
    const ticketUrl$ = this.coreApi.files.ticket(file.file_id, generateParams).pipe(
      map(({ ticket }) => {
        const search = paramsToQueryString({
          ...pick(file, ['version']),
          ticket,
          view: true,
        });
        const basePath = this.config.getApiUrl(`/api/files/${encodeURIComponent(file.file_id)}`);
        const baseUrl = basePath.startsWith('http') ? basePath : `${window.location.origin}${basePath}`;
        if (options?.member) {
          return `${baseUrl}/zip_member/${encodeURIComponent(options.member)}${search}`;
        }
        return `${baseUrl}/download${search}`;
      })
    );
    // attempt to get a signed URL, fall back to ticket endpoint if unsupported
    const signedUrl$ = this.coreApi.files.signedUrl(file.file_id, generateParams).pipe(
      catchError((error: HttpErrorResponse) => {
        // 409 == file not backed by cloud storage
        if (error.status === 409) {
          // get a ticket and build the URL to Core API
          return ticketUrl$;
        }
        throw error;
      })
    );
    return (options?.member ? ticketUrl$ : signedUrl$).pipe(
      map(fileUrl => ({
        params: { url: fileUrl },
      })),
    );
  }

  private sendArchiveMembers(message: ChannelMessage): Observable<MessageResponse> {
    const { params: { file } } = message;
    return this.coreApi.files.zipInfo(
      encodeURIComponent(file.file_id),
      pick(file, ['version']),
    ).pipe(map(info => ({
      params: { info },
    })));
  }

  private sendDataView(message: ChannelMessage): Observable<MessageResponse> {
    const { params: { id, body } } = message;
    return this.coreApi.views.preview(
      encodeURIComponent(id),
      body
    ).pipe(map(result => ({
      params: { result },
    })));
  }

  private sendSetting(message: ChannelMessage): Observable<MessageResponse> {
    const { params: { containerId, settingKey } } = message;
    let settingService$: Observable<ProjectSettings | SiteSettings>;

    if (['viewer_apps', 'deid_profile'].includes(settingKey)) {
      throw new Error(`${settingKey} is not defined in settings`);
    }

    if (containerId === 'site') {
      settingService$ = this.config.getSiteSettings();
    } else {
      settingService$ = this.coreApi.projects.settings.get(
        encodeURIComponent(containerId),
      );
    }

    return settingService$.pipe(
      map((result: SiteSettings | ProjectSettings) => {
        if (!(settingKey in result)) {
          throw new Error(`${settingKey} is not defined in settings`);
        }
        return {
          params: {
            result: result[settingKey],
          },
        };
      }),
    );
  }

  private writeSetting(message: ChannelMessage): Observable<MessageResponse> {
    const { params: { containerId, settingKey, data } } = message;
    let settingService$: Observable<null | ProjectSettings>;

    if (['viewer_apps', 'deid_profile'].includes(settingKey)) {
      throw new Error(`${settingKey} is not defined in settings`);
    }

    if (containerId === 'site') {
      settingService$ = this.config.setSiteSettings({
        [settingKey]: data,
      });

    } else {
      settingService$ = this.coreApi.projects.settings.put(
        encodeURIComponent(containerId),
        {
          [settingKey]: data,
        }
      );
    }

    return settingService$.pipe(map(result => ({
      params: { result },
    })));
  }

  private updateInfo(message: ChannelMessage): Observable<MessageResponse> {
    const { params: { container, file, info } } = message;
    let pipeline$: Observable<unknown>;
    if (file) {
      const fileId = encodeURIComponent(file.file_id);
      if (info.replace) {
        pipeline$ = this.coreApi.files.info.put(fileId, info.replace);
      } else if (info.set) {
        pipeline$ = this.coreApi.files.info.patch(fileId, info.set);
      } else if (info.delete) {
        pipeline$ = this.coreApi.files.info.get(fileId).pipe(
          switchMap(currentInfo =>
            this.coreApi.files.info.put(fileId, omit(currentInfo, info.delete))),
        );
      }
    } else if (container) {
      pipeline$ = this.coreApi.containers.info.patch(encodeURIComponent(container._id), info);
    } else {
      throw new Error('Container or file not provided');
    }
    return pipeline$.pipe(mapTo({
      params: {},
    }));
  }

  private uploadFile(message: ChannelMessage): Observable<MessageResponse> {
    const { params: { container, blob, options } } = message;
    if (!(blob instanceof Blob)) {
      throw new Error('File to be uploaded is not a Blob');
    }
    const formData = new FormData();
    formData.append('file', blob, options?.filename);
    const uploadParams = 'preserveMetadata' in options
      ? { preserve_metadata: Boolean(options.preserveMetadata) }
      : undefined;
    return this.coreApi.containers.files.upload(
      encodeURIComponent(container._id),
      formData,
      uploadParams,
    ).pipe(
      filter(event => event.type === HttpEventType.Response || event.type === HttpEventType.UploadProgress),
      map(event => {
        let params: FileUploadEvent;
        if (event.type === HttpEventType.Response) {
          params = { type: 'response', file: event.body[0] as File };
        } else if (event.type === HttpEventType.UploadProgress) {
          params = { type: 'progress', loaded: event.loaded, total: event.total };
        }
        return { params };
      }),
    );
  }

  private uploadText(message: ChannelMessage): Observable<MessageResponse> {
    if (this.saveText) {
      return this.saveText(message.params.text).pipe(map(() => ({})));
    }
    // Fallback to textSaved observable for editTextInMonacoViewer
    return this.textSaved ? this.textSaved.pipe(map(() => ({}))) : of({});
  }

  private addAnalysis(message: ChannelMessage): Observable<MessageResponse> {
    const { params: { parentId, addAnalysisInput, params } } = message;
    return this.coreApi.containers.analyses.post(parentId, addAnalysisInput, params)
      .pipe(map(_id => ({ params: { _id } })));
  }

  private addJob(message: ChannelMessage): Observable<MessageResponse> {
    const { params: { addJobInput } } = message;
    return this.coreApi.jobs.add(addJobInput).pipe(map(_id => ({
      params: { _id },
    })));
  }

  private addTag(message: ChannelMessage): Observable<MessageResponse> {
    const { params: { target, tag } } = message;
    const pipeline$ = isFile(target)
      ? this.coreApi.files.tags.patch(target.file_id, [tag])
      : this.coreApi.containers.tags.post(target._id, { value: tag });
    return pipeline$.pipe(mapTo({
      params: {},
    }));
  }

  private removeTag(message: ChannelMessage): Observable<MessageResponse> {
    const { params: { target, tag } } = message;
    const pipeline$ = isFile(target)
      ? this.coreApi.files.tags.delete(target.file_id, [tag])
      : this.coreApi.containers.tags.delete(target._id, tag);
    return pipeline$.pipe(mapTo({
      params: {},
    }));
  }

  private sendGearList(message: ChannelMessage): Observable<MessageResponse> {
    const { params: { query } } = message;
    return queryAll(this.coreApi.gears.query, query).pipe(map(result => ({
      params: { result },
    })));
  }
}

type MessageResponse = Partial<ChannelMessage>;

function getFileFromContainer(file: File, container: Container): File {
  const files: File[] = Array.isArray(container.inputs) ? [...container.inputs, ...container.files] : container.files;
  // file info from elastic doesn't have an _id -- fall back to name
  return file && (files.find(({ _id }) => _id && _id === file._id) ?? files.find(({ name }) => name && name === file.name));
}

function isFile(target: Container | File): target is File {
  return 'file_id' in target;
}
